import type { Locales } from "@/graphql/generated";
import { useContentValue } from "../hooks/api/content/useContentValue";
import type { Dictionary } from "./types";

const localeString = "fi-FI";

type CurrencyOptions = {
  minimumFractionsDigits?: number;
  suffix?: string | null;
  isUpdating?: boolean;
};

/**
 * Formats a number as a Euro currency string using the specified locale and options.
 *
 * @param val - The numeric value to format as currency.
 * @param options - Optional configuration for formatting.
 * @param options.minimumFractionsDigits - The minimum number of fraction digits to display.
 * @param options.suffix - An optional suffix to append to the formatted currency string, separated by ' / '.
 * @returns The formatted Euro currency string, optionally followed by the provided suffix.
 */
const formatEuro = (val: number, options?: CurrencyOptions) => {
  const currencyFormatter = new Intl.NumberFormat(localeString, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: options?.minimumFractionsDigits ?? 0,
  });
  return [
    options?.isUpdating === true ? "- €" : currencyFormatter.format(val),
    options?.suffix,
  ]
    .filter((val) => val !== undefined && val !== null)
    .join(" / ");
};

export const useMoneySuffix = () => {
  const { data: content } = useContentValue("globalContent");
  return content?.suffixForMonthYear;
};

/**
 * Formats a given date value as a Finnish locale date string.
 *
 * @param val - The date value to format. Can be a string, Date object, or null/undefined.
 * @returns The formatted date string in Finnish locale, or `undefined` if no valid date is provided.
 */
const formatFinnishDate = (val?: string | Date | null) => {
  let date = new Date();
  if (val !== undefined && val !== null) {
    if (typeof val === "string") {
      date = new Date(val);
    } else {
      date = val;
    }
    return date.toLocaleDateString(localeString);
  }
  return undefined;
};

/**
 * Replaces placeholders in the string with corresponding values from the provided dictionary.
 * Placeholders are defined using double curly braces, e.g., {{key}}.
 * If a key is not found in the dictionary, the placeholder remains unchanged.
 *
 * @param template - The string containing placeholders.
 * @param dict - A dictionary object containing key-value pairs to replace in the string.
 * @returns A new string with placeholders replaced by their corresponding values from the dictionary.
 *
 * @example
 * const template = "Hello, {{name}}!";
 * const result = formatString(template, { name: "Alice" }); // "Hello, Alice!"
 */
const formatString = (template: string, dict: Dictionary<string>): string =>
  template.replaceAll(/\{\{(\w+)\}\}/g, (_, key) => dict[key] ?? `{{${key}}}`);

const sanitizeDuplicateLanguageInUrl = (url: string, language: Locales) => {
  const segments = url.split("/");
  const languageIndexes = segments.reduce<number[]>((acc, segment, index) => {
    if (segment === language) {
      acc.push(index);
    }

    return acc;
  }, []);

  if (languageIndexes.length <= 1) {
    return url;
  }

  segments.splice(languageIndexes[0], 1);
  return segments.join("/");
};

const sanitizeDuplicateSlashesInUrl = (url: string) => {
  const protocolSeparator = "://";
  const protocolIndex = url.indexOf(protocolSeparator);

  if (protocolIndex === -1) {
    return url.replaceAll(/\/{2,}/g, "/");
  }

  const protocol = url.slice(0, protocolIndex);
  const rest = url.slice(protocolIndex + protocolSeparator.length);
  return `${protocol}${protocolSeparator}${rest.replaceAll(/\/{2,}/g, "/")}`;
};

export {
  formatEuro,
  formatFinnishDate,
  formatString,
  sanitizeDuplicateLanguageInUrl,
  sanitizeDuplicateSlashesInUrl,
};
